import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { createAuthGuard } from './guards'
import type { AdminRouteMeta } from './guards'

/**
 * Augment Vue Router's RouteMeta to include admin-specific fields.
 */
declare module 'vue-router' {
  interface RouteMeta extends Partial<AdminRouteMeta> {}
}

/**
 * Route definitions for the Postlytics Admin panel.
 *
 * All views are lazy-loaded via dynamic import().
 * Each route includes meta with: guard, layout, pageTitle, and optional requiredRoles.
 *
 * SECURITY:
 * - Routes NEVER point to /api/v1/* endpoints
 * - All API calls use /api/admin/* ONLY
 * - Guards enforce aud: "admin" JWT, MFA completion, and role-based access
 */
const routes: RouteRecordRaw[] = [
  // --- Auth routes (AuthLayout) ---
  {
    path: '/admin/login',
    name: 'admin-login',
    component: () => import('@/views/auth/AdminLoginView.vue'),
    meta: {
      guard: 'guest-only',
      layout: 'AuthLayout',
      pageTitle: 'Login',
    },
  },
  {
    path: '/admin/mfa/setup',
    name: 'admin-mfa-setup',
    component: () => import('@/views/auth/MfaSetupView.vue'),
    meta: {
      guard: 'auth+mfa-needed',
      layout: 'AuthLayout',
      pageTitle: 'MFA Setup',
    },
  },

  // --- Session Management (AdminLayout, all admin roles) ---
  {
    path: '/admin/sessions',
    name: 'admin-sessions',
    component: () => import('@/views/auth/SessionManagementView.vue'),
    meta: {
      guard: 'auth+mfa',
      layout: 'AdminLayout',
      pageTitle: 'Active Sessions',
    },
  },

  // --- Dashboard (AdminLayout) ---
  {
    path: '/admin/dashboard',
    name: 'admin-dashboard',
    component: () => import('@/views/dashboard/AdminDashboardView.vue'),
    meta: {
      guard: 'auth+mfa',
      layout: 'AdminLayout',
      pageTitle: 'Dashboard',
    },
  },

  // --- Organizations (AdminLayout) ---
  {
    path: '/admin/organizations',
    name: 'admin-organizations',
    component: () => import('@/views/organizations/OrgListView.vue'),
    meta: {
      guard: 'auth+mfa',
      layout: 'AdminLayout',
      pageTitle: 'Organizations',
    },
  },
  {
    path: '/admin/organizations/:orgId',
    name: 'admin-organization-detail',
    component: () => import('@/views/organizations/OrgDetailView.vue'),
    meta: {
      guard: 'auth+mfa',
      layout: 'AdminLayout',
      pageTitle: 'Organization Detail',
    },
  },

  // --- Users (AdminLayout) ---
  {
    path: '/admin/users',
    name: 'admin-users',
    component: () => import('@/views/users/UserListView.vue'),
    meta: {
      guard: 'auth+mfa',
      layout: 'AdminLayout',
      pageTitle: 'Users',
    },
  },
  {
    path: '/admin/users/:userId',
    name: 'admin-user-detail',
    component: () => import('@/views/users/UserDetailView.vue'),
    meta: {
      guard: 'auth+mfa',
      layout: 'AdminLayout',
      pageTitle: 'User Detail',
    },
  },

  // --- Plans (AdminLayout, role-gated) ---
  {
    path: '/admin/plans',
    name: 'admin-plans',
    component: () => import('@/views/plans/PlanListView.vue'),
    meta: {
      guard: 'auth+mfa+role',
      layout: 'AdminLayout',
      pageTitle: 'Plans',
      requiredRoles: ['super_admin', 'billing'],
    },
  },
  {
    path: '/admin/plans/new',
    name: 'admin-plan-new',
    component: () => import('@/views/plans/PlanFormView.vue'),
    meta: {
      guard: 'auth+mfa+role',
      layout: 'AdminLayout',
      pageTitle: 'New Plan',
      requiredRoles: ['super_admin', 'billing'],
    },
  },
  {
    path: '/admin/plans/:planId/edit',
    name: 'admin-plan-edit',
    component: () => import('@/views/plans/PlanFormView.vue'),
    meta: {
      guard: 'auth+mfa+role',
      layout: 'AdminLayout',
      pageTitle: 'Edit Plan',
      requiredRoles: ['super_admin', 'billing'],
    },
  },

  // --- Admin Users (AdminLayout, super_admin only) ---
  {
    path: '/admin/admin-users',
    name: 'admin-admin-users',
    component: () => import('@/views/admin-users/AdminUserListView.vue'),
    meta: {
      guard: 'auth+mfa+role',
      layout: 'AdminLayout',
      pageTitle: 'Admin Users',
      requiredRoles: ['super_admin'],
    },
  },
  {
    path: '/admin/admin-users/new',
    name: 'admin-admin-user-new',
    component: () => import('@/views/admin-users/AdminUserFormView.vue'),
    meta: {
      guard: 'auth+mfa+role',
      layout: 'AdminLayout',
      pageTitle: 'New Admin User',
      requiredRoles: ['super_admin'],
    },
  },
  {
    path: '/admin/admin-users/:adminUserId/edit',
    name: 'admin-admin-user-edit',
    component: () => import('@/views/admin-users/AdminUserFormView.vue'),
    meta: {
      guard: 'auth+mfa+role',
      layout: 'AdminLayout',
      pageTitle: 'Edit Admin User',
      requiredRoles: ['super_admin'],
    },
  },

  // --- Support Tools (AdminLayout, role-gated) ---
  {
    path: '/admin/support',
    name: 'admin-support',
    component: () => import('@/views/support/SupportToolsView.vue'),
    meta: {
      guard: 'auth+mfa+role',
      layout: 'AdminLayout',
      pageTitle: 'Support Tools',
      requiredRoles: ['super_admin', 'support'],
    },
  },

  // --- System Health (AdminLayout, super_admin only) ---
  {
    path: '/admin/system/health',
    name: 'admin-system-health',
    component: () => import('@/views/system/SystemHealthView.vue'),
    meta: {
      guard: 'auth+mfa+role',
      layout: 'AdminLayout',
      pageTitle: 'System Health',
      requiredRoles: ['super_admin'],
    },
  },

  // --- Platform Health (AdminLayout, super_admin only) ---
  {
    path: '/admin/system/platform-health',
    name: 'admin-platform-health',
    component: () => import('@/views/system/PlatformHealthView.vue'),
    meta: {
      guard: 'auth+mfa+role',
      layout: 'AdminLayout',
      pageTitle: 'Platform Health',
      requiredRoles: ['super_admin'],
    },
  },

  // --- Feature Flags (AdminLayout, super_admin only) ---
  {
    path: '/admin/system/feature-flags',
    name: 'admin-feature-flags',
    component: () => import('@/views/system/FeatureFlagsView.vue'),
    meta: {
      guard: 'auth+mfa+role',
      layout: 'AdminLayout',
      pageTitle: 'Feature Flags',
      requiredRoles: ['super_admin'],
    },
  },

  // --- Subscriptions (AdminLayout, role-gated: super_admin, billing) ---
  {
    path: '/admin/subscriptions',
    name: 'admin-subscriptions',
    component: () => import('@/views/subscriptions/SubscriptionListView.vue'),
    meta: {
      guard: 'auth+mfa+role',
      layout: 'AdminLayout',
      pageTitle: 'Subscriptions',
      requiredRoles: ['super_admin', 'billing'],
    },
  },
  {
    path: '/admin/subscriptions/:subId',
    name: 'admin-subscription-detail',
    component: () => import('@/views/subscriptions/SubscriptionDetailView.vue'),
    meta: {
      guard: 'auth+mfa+role',
      layout: 'AdminLayout',
      pageTitle: 'Subscription Detail',
      requiredRoles: ['super_admin', 'billing'],
    },
  },

  // --- Audit Logs (AdminLayout, all admin roles) ---
  {
    path: '/admin/audit-logs',
    name: 'admin-audit-logs',
    component: () => import('@/views/audit/AuditLogView.vue'),
    meta: {
      guard: 'auth+mfa',
      layout: 'AdminLayout',
      pageTitle: 'Audit Logs',
    },
  },

  // --- Root redirect ---
  {
    path: '/',
    redirect: '/admin/login',
  },

  // --- Catch-all 404 ---
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/errors/NotFoundView.vue'),
    meta: {
      guard: 'none',
      layout: 'AuthLayout',
      pageTitle: 'Not Found',
    },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

// Register global auth guard
router.beforeEach(createAuthGuard())

export default router
